import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { authService, User, LoginResponse } from "../services/authService";
import { setMemoryToken } from "../services/api";

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;
  isLoading: boolean;
  error: string | null;
  setHasHydrated: (value: boolean) => void;
  login: (email: string, password: string) => Promise<LoginResponse>;
  loginWithSSO: (token: string) => Promise<LoginResponse>;
  logout: () => void;
  clearError: () => void;
  checkTokenExpiration: () => boolean;
}

// Função para decodificar JWT e verificar expiração
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp * 1000; // Converter para milliseconds
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      hasHydrated: false,
      isLoading: false,
      error: null,
      setHasHydrated: (value: boolean) => set({ hasHydrated: value }),

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);
          
          console.log('[AuthStore] Login response:', {
            hasToken: !!response.token,
            tokenPreview: response.token?.substring(0, 20),
            user: response.user
          });
          
          // Define o token em memória IMEDIATAMENTE
          setMemoryToken(response.token);
          console.log('[AuthStore] setMemoryToken chamado');
          
          set({
            token: response.token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          
          console.log('[AuthStore] Estado atualizado');
          
          return response;
        } catch (error: any) {
          const message = error.response?.data?.error || "Erro ao fazer login";
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        setMemoryToken(null);
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
        });
        sessionStorage.removeItem("auth-storage");
      },

      loginWithSSO: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.ssoLogin(token);
          setMemoryToken(response.token);
          set({
            token: response.token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          return response;
        } catch (error: any) {
          const message = error.response?.data?.error || "Erro ao autenticar via SSO";
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      clearError: () => set({ error: null }),

      checkTokenExpiration: () => {
        const { token, isAuthenticated } = get();
        if (isAuthenticated && isTokenExpired(token)) {
          get().logout();
          return false;
        }
        // Sincroniza o memoryToken
        if (isAuthenticated && token) {
          setMemoryToken(token);
        }
        return isAuthenticated;
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.token && isTokenExpired(state.token)) {
          state.logout();
        } else if (state && state.token) {
          // Sincroniza token com memória ao rehidratar
          setMemoryToken(state.token);
        }
        state?.setHasHydrated(true);
      },
    }
  )
);
